import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ListStateAction } from '../../actions/ListStateAction';
import { ORG_HQ } from '../../config/appConstants';
import { Translate as t } from '../../config/Translate';
import { findMatchingNodes, getAncestorIds } from './treeSearch';

// IMP-373: 트리 초기 렌더 시 기본으로 펼칠 깊이. ALL(depth 1) 과 그 직속 자식(depth 2)까지만 펼치고
// depth 2 노드의 자식(depth 3 이하)은 접은 상태로 표시. (검색/토글로 개별 확장은 그대로 동작)
const DEFAULT_EXPAND_DEPTH = 2;

// IMP-380: 형제 노드를 국가 코드 오름차순 → 이름 오름차순으로 정렬한다. 단 국가 코드가 빈값(미지정)인
// 노드는 하단(맨 뒤)에 배치. 트리 렌더 순서(형제 그룹)에서만 정렬하며 CompanyReducer/콤보는 무관.
const byTreeCountryName = (a, b) => {
    const ca = a?.country || '';
    const cb = b?.country || '';
    if (ca !== cb) {
        if (!ca) return 1;  // 빈 국가는 하단
        if (!cb) return -1;
        return ca.localeCompare(cb);
    }
    return String(a?.name || '').localeCompare(String(b?.name || ''));
};

const RayTreeviewParent = ({ item, callback, expanded = true }) => {
    const [view, setView] = useState(expanded);
    const [icon, setIcon] = useState(expanded ? "/assets/img/minus3.gif" : "/assets/img/plus3.gif");
    const toggleShowHide = (e) => {
        // IMP-373(M1): 검색 expandAndScrollTo 가 DOM display 를 직접 바꿀 수 있어 local view 와 어긋난다.
        // 실제 DOM 의 현재 display 를 SSoT 로 읽어 아이콘/상태를 결정 → callback(DOM 토글)과 일관.
        const elem = document.getElementById("tree-child-" + item.id);
        const isCurrentlyShown = elem ? elem.style.display !== 'none' : view;
        const willShow = !isCurrentlyShown; // 클릭 후 펼쳐질지
        setIcon(willShow ? "/assets/img/minus3.gif" : "/assets/img/plus3.gif");
        setView(willShow);
        callback && callback(item);
    }
    return <img src={icon} onClick={(e) => toggleShowHide(e)} style={{ height: 18, width: 18 }} />;
}

const RayTreeviewItem = ({ item, isParent = false, click, selected, matchStyle }) => {
    return <>
        {!isParent && <img src="/assets/img/line3.gif" style={{ height: 18, width: 18 }} />}
        <span className="cur-pointer" onClick={click}>
            <img src="/assets/img/RayIOTTreeMap.png" />
            <span
                className={selected == item.id ? "cur-pointer rtvselected" : "cur-pointer"}
                style={matchStyle || undefined}
                // IMP-332: 좁은 폭에서 이름이 클립될 때 hover 로 전체 이름 확인
                title={(item.country ? "[" + item.country + "] " : "") + (item.name || "")}
            >
                {item.country && "[" + item.country + "] "}{item.name}
            </span>
        </span>
    </>
}

const RayTreeview = ({ companies, onClickItem, onResize, checkbox = false, maxHeight = 600, user, stateKey, listStates }) => {
    const dispatch = useDispatch();

    // IMP-307: stateKey 있으면 트리 검색어/선택 복원. 표 스냅샷과 동일 슬롯의 .tree 하위에 저장(merge).
    const treeSnap = stateKey ? (listStates && listStates[stateKey] && listStates[stateKey].tree) : undefined;

    const [vHg, setvHg] = useState(0);
    const [selected, setSelected] = useState(treeSnap?.selectedId ?? 0);
    const [list, setList] = useState([]);

    // FEAT-036: 검색 상태
    const [keyword, setKeyword] = useState(treeSnap?.keyword ?? '');
    const [matchIds, setMatchIds] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

    const getHeight = () => setvHg(window.innerHeight);

    // FEAT-036: 직전 keyword 추적 — list refresh(데이터 갱신)와 keyword 변경 경로 구분
    const prevKeywordRef = useRef(keyword);

    // IMP-334: treeView useMemo 가 itemSelected 클로저를 캐시하는 동안(deps 미변경 재렌더)에도
    // keyword/onClickItem 최신값을 읽도록 ref 로 노출. deps 미포함으로 memo 효율은 유지하면서
    // stale closure(타이핑 직후 클릭 시 직전 keyword/onClickItem 참조) 회귀를 방지.
    const keywordRef = useRef(keyword);
    keywordRef.current = keyword;
    const onClickItemRef = useRef(onClickItem);
    onClickItemRef.current = onClickItem;

    // FEAT-036: 매칭 노드의 조상을 DOM에서 펼치고 해당 노드로 스크롤
    const expandAndScrollTo = useCallback((id) => {
        const ancestorIds = getAncestorIds(list, id);
        ancestorIds.forEach(ancestorId => {
            const elem = document.getElementById(`tree-child-${ancestorId}`);
            if (elem) elem.style.display = 'block';
        });
        setTimeout(() => {
            document.getElementById(`tree-node-${id}`)?.scrollIntoView({ block: 'nearest' });
        }, 0);
    }, [list]);

    // FEAT-036: 매칭 노드 재계산. keyword/list 변경 시 강조는 항상 최신화하되,
    // 첫 매칭 스크롤·인덱스 리셋은 "keyword 변경(사용자 검색)" 시에만 수행.
    // (데이터 refresh 로 list 만 바뀔 때 의도치 않은 스크롤 이동 방지)
    useEffect(() => {
        const ids = findMatchingNodes(list, keyword);
        setMatchIds(ids);
        if (prevKeywordRef.current !== keyword) {
            setCurrentMatchIndex(0);
            if (ids.length > 0) {
                expandAndScrollTo(ids[0]);
            }
        }
        prevKeywordRef.current = keyword;
    }, [keyword, list, expandAndScrollTo]);

    const handlePrev = () => {
        if (matchIds.length === 0) return;
        const nextIndex = (currentMatchIndex - 1 + matchIds.length) % matchIds.length;
        setCurrentMatchIndex(nextIndex);
        expandAndScrollTo(matchIds[nextIndex]);
    };

    const handleNext = () => {
        if (matchIds.length === 0) return;
        const nextIndex = (currentMatchIndex + 1) % matchIds.length;
        setCurrentMatchIndex(nextIndex);
        expandAndScrollTo(matchIds[nextIndex]);
    };

    useEffect(() => {
        genTreeviewData();
    }, [companies]);

    useEffect(() => {
        //GetCompany();
        window.addEventListener('resize', getHeight);
        setvHg(window.innerHeight);
        return () => window.removeEventListener('resize', getHeight);
    }, []);

    const genTreeviewData = () => {
        const treeNode = []
        if (user.companyInfo.org == ORG_HQ) {
            treeNode.push({ id: "0", name: "ALL", parent: "", companyId: "", scp: '' });
        } else {
            const rtUser = JSON.parse(localStorage.getItem("rtUser"));
            treeNode.push({ id: user.companyInfo.companyId, name: user.companyInfo.name, parent: "", companyId: user.companyInfo.companyId, scp: rtUser?.scp });
        }

        companies.map((u) => {
            let scp ='';

            if (u.scp_1 >= 0) scp += u.scp_1 + ':';
            if (u.scp_2 >= 0) scp += u.scp_2 + ':';
            if (u.scp_3 >= 0) scp += u.scp_3 + ':';

            treeNode.push({
                id: u.companyId,
                name: u.name,
                country: u.country,
                parent: u.parentCompanyId || "0",
                scp
            });
        });

        setList(treeNode);
    }

    // IMP-307: 복원된 선택 회사를 부모에 1회 전달(라벨 복원). list 준비 후 실행. setSelected 는 이미 초기값 복원됨.
    const treeRestoredRef = useRef(false);
    useEffect(() => {
        if (!stateKey) return;
        if (treeRestoredRef.current) return;
        if (!treeSnap || !treeSnap.selectedId) return;
        if (!list || list.length === 0) return;
        treeRestoredRef.current = true;
        if (onClickItem) onClickItem(treeSnap.filterCompany || { id: treeSnap.selectedId });
        // list 준비 시점에만 1회 복원하면 충분(treeRestoredRef 가드). stateKey/treeSnap/onClickItem 은 클로저 캡처로 안전.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list]);

    // FEAT-036: 매칭 id→index Map (노드별 indexOf O(N·M) → O(1) 조회)
    const matchIndexMap = useMemo(() => {
        const m = new Map();
        matchIds.forEach((id, i) => m.set(id, i));
        return m;
    }, [matchIds]);

    // IMP-334: parent별 children Map — CreateTree 가 노드마다 list 전체를 filter(O(N))
    // 하던 것을 O(1) 조회로 대체(트리 전체 O(N²)→O(N)). 루트 판정은 기존 `parent == ""` 과 동치.
    // id/parent 는 항상 String(companyId/parentCompanyId) 이지만 안전하게 String() 통일.
    const childrenByParent = useMemo(() => {
        const map = new Map();
        list.forEach((node) => {
            const key = node.parent === "" ? "" : String(node.parent);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(node);
        });
        // IMP-380: 형제 그룹을 국가 > 이름 순으로 정렬(빈 국가는 하단). 루트는 단독 그룹이라 위치 불변.
        map.forEach((siblings) => siblings.sort(byTreeCountryName));
        return map;
    }, [list]);

    const getMatchStyle = (id) => {
        if (matchIds.length === 0) return null;
        const matchIdx = matchIndexMap.get(id);
        if (matchIdx === undefined) return null;
        // 선택된 노드는 검색 강조로 덮지 않고 선택 스타일(rtvselected: 진회색 배경)을 우선 노출 → 무엇이 선택됐는지 명확.
        // 단, 이동버튼으로 액티브된 현재 검색 위치를 선택한 경우엔 주황 테두리를 덧대어 일반 선택과 구분.
        if (id == selected) {
            return matchIdx === currentMatchIndex ? { outline: '2px solid #f5a623' } : null;
        }
        // 글자색을 항상 어둡게 강제 — rtvselected(선택 시 흰 글자)와 겹쳐도 노란 배경 위 가독성 확보
        if (matchIdx === currentMatchIndex) {
            return { backgroundColor: '#ffe066', outline: '2px solid #f5a623', color: '#212529' };
        }
        return { backgroundColor: '#fff9c4', color: '#212529' };
    };

    const CreateTree = (data, isSub = false, lev) => {
        const level = lev || 1;
        const children = [];
        (!isSub ? (childrenByParent.get("") ?? []) : data).map((x,idx) => {
            const sublist = childrenByParent.get(String(x.id)) ?? [];
            const matchStyle = getMatchStyle(x.id);
            const expanded = level < DEFAULT_EXPAND_DEPTH; // IMP-373
            if (sublist.length > 0) {
                children.push(
                    <li key={idx}
                        id={`tree-node-${x.id}`}
                        style={{
                            backgroundImage: "url(/assets/img/line1.gif)",
                            backgroundRepeat: "repeat-y"
                        }}>
                        {<RayTreeviewParent item={x} callback={(item) => toggleShowHide(x.id)} expanded={expanded} />}
                        <RayTreeviewItem item={x} click={(e) => itemSelected(x)} isParent={true} selected={selected} matchStyle={matchStyle} />
                        <ul id={`tree-child-${x.id}`} style={{ "display": expanded ? "block" : "none", paddingLeft: 18 }}>
                            {CreateTree(sublist, true, level + 1)}
                        </ul>
                    </li>
                );
            } else {
                children.push(
                    <li key={idx}
                        id={`tree-node-${x.id}`}
                        style={{
                            backgroundImage: "url(/assets/img/line1.gif)",
                            backgroundRepeat: "repeat-y"
                        }}>
                        <RayTreeviewItem item={x} click={(e) => itemSelected(x)} selected={selected} matchStyle={matchStyle} />
                    </li>
                );
            }
        })
        return children;
    }

    const toggleShowHide = (id) => {
        const elem = document.getElementById("tree-child-" + id);
        if (!elem) return;
        if (elem.style.display === 'block') {
            elem.style.display = "none";
        } else {
            elem.style.display = "block";
        }
    }

    // IMP-307: 사용자가 검색어/선택 변경 시에만 저장. 복원(useState 초기값/onClickItem)은 저장 트리거 아님.
    const saveTreeState = (kw, sid, item) => {
        if (!stateKey) return;
        dispatch(ListStateAction.Save(stateKey, { tree: { keyword: kw, selectedId: sid, filterCompany: item } }));
    };

    const itemSelected = (item) => {
        const sid = item.id === "0" ? "0" : item.id;
        setSelected(item.id);
        const payload = { ...item, id: sid };
        // IMP-334: onClickItem/keyword 는 treeView useMemo 에 캐시된 클로저에서 호출될 수 있어
        // ref.current 로 최신값 참조(stale closure 방지 — 타이핑 직후 클릭해도 최신 keyword 저장).
        onClickItemRef.current && onClickItemRef.current(payload);
        saveTreeState(keywordRef.current, sid, payload); // IMP-307
    }

    // IMP-334: 트리 출력을 useMemo 로 캐시 — resize(vHg) 등 트리 출력과 무관한 재렌더에서
    // CreateTree 재구성을 건너뜀. deps 는 트리 출력에 실제 영향을 주는 값만 나열
    // (getMatchStyle 이 참조하는 matchIndexMap 은 matchIds 파생값이라 별도 deps 불필요).
    // itemSelected 가 참조하는 keyword/onClickItem 은 ref(keywordRef/onClickItemRef)로 최신값을
    // 읽으므로 deps 미포함이어도 stale closure 문제 없음(Major 리뷰 반영, IMP-334).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const treeView = useMemo(() => {
        return list.length > 0 ? CreateTree(list) : "";
    }, [list, selected, matchIds, currentMatchIndex, childrenByParent]);

    // FEAT-036: 검색 컨트롤 공통 높이(input·버튼 정렬용) + 헤더 하단 여백
    const SEARCH_CONTROL_HEIGHT = 31;
    const SEARCH_HEADER_PADDING_BOTTOM = 6;
    // 트리 카드 높이에서 차감할 검색 헤더 전체 높이
    const SEARCH_HEADER_HEIGHT = SEARCH_CONTROL_HEIGHT + SEARCH_HEADER_PADDING_BOTTOM;

    const RESIZE_MIN_WIDTH = 200;
    const RESIZE_MAX_WIDTH = 600;
    const RESIZE_HANDLE_WIDTH = 8;
    const RESIZE_HANDLE_OFFSET = -4;
    const RESIZE_HANDLE_Z_INDEX = 10;

    const containerRef = useRef(null);
    const listenersRef = useRef({ onMove: null, onUp: null });

    const handleResizeMouseDown = useCallback((e) => {
        if (!onResize) return;
        e.preventDefault();
        const startX = e.clientX;
        const startW = containerRef.current?.offsetWidth ?? 300;
        const onMove = (ev) => {
            const newW = Math.max(RESIZE_MIN_WIDTH, Math.min(RESIZE_MAX_WIDTH, startW + ev.clientX - startX));
            onResize(newW);
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            listenersRef.current = { onMove: null, onUp: null };
        };
        listenersRef.current = { onMove, onUp };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [onResize]);

    useEffect(() => {
        return () => {
            const { onMove, onUp } = listenersRef.current;
            if (onMove) document.removeEventListener('mousemove', onMove);
            if (onUp) document.removeEventListener('mouseup', onUp);
        };
    }, []);

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            {/* FEAT-036: 키워드 검색 영역 — 스크롤 카드 바깥(상단 고정) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: `0 2px ${SEARCH_HEADER_PADDING_BOTTOM}px` }}>
                <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t('common.button.search')}
                    value={keyword}
                    onChange={ev => { setKeyword(ev.target.value); saveTreeState(ev.target.value, selected, list.find(x => x.id === selected) ?? null); }}
                    style={{ flex: 1, minWidth: 0, height: SEARCH_CONTROL_HEIGHT }}
                />
                {keyword.trim() !== '' && (
                    <>
                        <span style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#555' }}>
                            {matchIds.length > 0 ? currentMatchIndex + 1 : 0} / {matchIds.length}
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handlePrev}
                            disabled={matchIds.length === 0}
                            style={{ height: SEARCH_CONTROL_HEIGHT, padding: '0 8px', lineHeight: 1, display: 'flex', alignItems: 'center', margin: '0px'}}
                        >↑</button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleNext}
                            disabled={matchIds.length === 0}
                            style={{ height: SEARCH_CONTROL_HEIGHT, padding: '0 8px', lineHeight: 1, display: 'flex', alignItems: 'center', margin: '0px' }}
                        >↓</button>
                    </>
                )}
            </div>
            {/* IMP-332: 가로 스크롤 없이 넘치는 이름은 클립(overflowX hidden), 세로는 스크롤 유지 */}
            <div className="card raytreeview p-1" style={{ "maxHeight": vHg - 160 - SEARCH_HEADER_HEIGHT, "overflowX": "hidden", "overflowY": "auto" }}>
                <ul>
                    {treeView}
                </ul>
            </div>
            {onResize && <div
                style={{
                    position: 'absolute', top: 0, right: RESIZE_HANDLE_OFFSET, width: RESIZE_HANDLE_WIDTH, height: '100%',
                    cursor: 'col-resize', zIndex: RESIZE_HANDLE_Z_INDEX
                }}
                onMouseDown={handleResizeMouseDown}
            />}
        </div>
    );
}

const mapState = (state) => {
    const user = state.AuthReducer.user;
    const filter = state.AdminUserReducer.filter;
    const companies = state.CompanyReducer.companies;
    const listStates = state.ListStateReducer;
    return { companies, filter, user, listStates };
};

const mapDispatch = (dispatch) => ({
    //GetCompany: () => dispatch(CompanyAction.GetCompany())
});

export default connect(mapState, mapDispatch)(RayTreeview);
